import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as walletService from './walletService';
import * as loanService from './loanService';
import * as goldService from './goldService';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function formatDate(unixSeconds) {
  try {
    return new Date(parseInt(unixSeconds, 10) * 1000).toLocaleString();
  } catch {
    return String(unixSeconds);
  }
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Translate a raw DB transaction type (deposit/withdrawal/disbursement/...) to a
// localized label. Falls back to capitalized raw value for unknown types.
function txTypeLabel(type, t) {
  const map = {
    deposit: 'deposit',
    withdrawal: 'withdrawal',
    withdraw: 'withdraw',
    adjustment: 'adjustment',
    transfer: 'transfer',
    disbursement: 'disbursement',
    repayment: 'repayment',
  };
  const key = map[(type || '').toLowerCase()];
  if (!key) return capitalize(type);
  const label = t(key);
  // If no translation found (t returns the key), fall back to capitalized raw.
  return label === key ? capitalize(type) : label;
}

// ---- Branding (matches dashboard theme.css) ----
// Primary green + yellow accent; dark slate for body text.
const BRAND = {
  primary: [22, 163, 74],     // --primary #16a34a
  primaryDark: [15, 110, 50], // darker green for gradients/footer
  accent: [245, 208, 108],    // --accent #f5d06c
  text: [30, 41, 59],         // --text light #1e293b
  muted: [100, 116, 139],     // --muted #64748b
  positive: [22, 163, 74],
  negative: [220, 38, 38],
  rowAlt: [240, 248, 242],    // faint green tint for zebra rows
};

const STORE_NAME = 'GL Compra de Ouro';
const STORE_ADDRESS = 'ANAMOESTRAAT #15';

// Cache the logo dataURL so we only fetch+decode once per session.
let _logoDataUrlPromise = null;

/**
 * Load the company logo as a PNG dataURL (required by jsPDF.addImage).
 * Returns null on any failure so export still works without the logo.
 * @returns {Promise<string|null>}
 */
function loadLogoDataUrl() {
  if (_logoDataUrlPromise) return _logoDataUrlPromise;

  _logoDataUrlPromise = (async () => {
    try {
      const url = (process.env.PUBLIC_URL || '') + '/assets/logo.png';
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  })();

  return _logoDataUrlPromise;
}

/**
 * Draw the branded PDF header: logo (left), store name + statement title.
 * Returns the Y coordinate to continue drawing below the header.
 * @param {jsPDF} doc
 * @param {string} title Statement title (already localized)
 * @param {string|null} logo dataURL or null
 * @param {number} marginX
 * @param {number} pageWidth
 * @param {(key:string)=>string} t
 * @returns {number} next Y
 */
function drawBrandedHeader(doc, title, logo, marginX, pageWidth, t) {
  const top = 36;
  let textX = marginX;

  // Logo block
  if (logo) {
    const logoW = 54;
    const logoH = 54;
    try {
      doc.addImage(logo, 'PNG', marginX, top, logoW, logoH);
      textX = marginX + logoW + 14;
    } catch {
      textX = marginX;
    }
  }

  // Store name (green) + address (muted)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...BRAND.primary);
  doc.text(STORE_NAME, textX, top + 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(STORE_ADDRESS, textX, top + 34);

  // Statement title (right aligned, dark)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...BRAND.text);
  doc.text(title, pageWidth - marginX, top + 20, { align: 'right' });

  // Generated timestamp under title
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(`${t('generated')}: ${new Date().toLocaleString()}`, pageWidth - marginX, top + 34, { align: 'right' });

  // Accent divider rule
  const ruleY = top + 48;
  doc.setDrawColor(...BRAND.accent);
  doc.setLineWidth(2);
  doc.line(marginX, ruleY, pageWidth - marginX, ruleY);
  doc.setTextColor(0);

  return ruleY + 18;
}

/**
 * Draw the branded footer on the current page: accent rule, store name (green,
 * left) and page number (muted, right).
 * @param {jsPDF} doc
 * @param {number} marginX
 * @param {number} pageWidth
 * @param {(key:string)=>string} t
 */
function drawBrandedFooter(doc, marginX, pageWidth, t) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const footY = pageHeight - 24;

  // Accent rule above footer
  doc.setDrawColor(...BRAND.accent);
  doc.setLineWidth(1);
  doc.line(marginX, footY - 10, pageWidth - marginX, footY - 10);

  const pageCount = doc.internal.getNumberOfPages();
  const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.primary);
  doc.text(STORE_NAME, marginX, footY);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);
  doc.text(`${t('page')} ${pageCurrent} ${t('of')} ${pageCount}`, pageWidth - marginX, footY, { align: 'right' });
  doc.setTextColor(0);
}

/**
 * Generate and download a PDF of a customer's full transaction history.
 * Each row shows the transaction details plus the balance (balance_after) at that time.
 * @param {string|number} customerId
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function exportCustomerTransactionsPDF(customerId, t) {
  const result = await walletService.getTransactionsExport(customerId);

  if (!result.success || !result.data) {
    return { success: false, message: result.message || 'Failed to load export data' };
  }

  const { customer, balances = {}, transactions = [] } = result.data;

  const logo = await loadLogoDataUrl();

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Branded header (logo + store + title + accent rule)
  let y = drawBrandedHeader(doc, t('transaction_statement'), logo, marginX, pageWidth, t);

  // Customer info block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.text);
  doc.text(customer.name || t('unknown_customer'), marginX, y);

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);

  if (customer.phone) {
    doc.text(`${t('phone')}: ${customer.phone}`, marginX, y);
    y += 14;
  }
  if (customer.info) {
    const infoLines = doc.splitTextToSize(`${t('additional_info')}: ${customer.info}`, pageWidth - marginX * 2);
    doc.text(infoLines, marginX, y);
    y += 14 * infoLines.length;
  }
  y += 18;

  // Current balances summary
  const balanceParts = Object.keys(balances).length
    ? Object.entries(balances).map(([cur, amt]) => `${cur}: ${formatAmount(amt)}`)
    : [t('no_wallets')];
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.primary);
  doc.text(t('current_balances'), marginX, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.text);
  doc.text(balanceParts.join('    '), marginX, y);
  doc.setTextColor(0);
  y += 10;

  // Transactions table
  // Track running balances for all three currencies, carrying forward unchanged ones.
  // The transaction's own currency uses the authoritative balance_after from the DB.
  const running = { USD: 0, EUR: 0, SRD: 0 };
  const head = [[t('date'), t('type'), t('description'), t('amount'), 'USD', 'EUR', 'SRD']];
  const body = transactions.map((tx) => {
    const amount = parseFloat(tx.amount);
    const sign = amount >= 0 ? '+' : '-';
    const cur = tx.currency;

    // Update the affected currency to its authoritative post-transaction balance
    if (cur in running) {
      running[cur] = parseFloat(tx.balance_after);
    }

    return [
      formatDate(tx.created_at),
      txTypeLabel(tx.type, t),
      tx.description || '-',
      `${sign}${formatAmount(Math.abs(amount))} ${cur}`,
      formatAmount(running.USD),
      formatAmount(running.EUR),
      formatAmount(running.SRD),
    ];
  });

  autoTable(doc, {
    startY: y + 10,
    head,
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BRAND.rowAlt },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 70 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 105, halign: 'right' },
      4: { cellWidth: 75, halign: 'right' },
      5: { cellWidth: 75, halign: 'right' },
      6: { cellWidth: 75, halign: 'right' },
    },
    didParseCell: (data) => {
      // Color amount column based on sign
      if (data.section === 'body' && data.column.index === 3) {
        const raw = data.cell.raw || '';
        if (typeof raw === 'string' && raw.startsWith('-')) {
          data.cell.styles.textColor = BRAND.negative;
        } else {
          data.cell.styles.textColor = BRAND.positive;
        }
      }
    },
    didDrawPage: (data) => {
      drawBrandedFooter(doc, marginX, pageWidth, t);
    },
  });

  if (transactions.length === 0) {
    const afterY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(t('no_transactions_found_for_customer'), marginX, afterY + 24);
    doc.setTextColor(0);
  }

  const safeName = (customer.name || 'customer').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`statement_${safeName}_${dateStamp}.pdf`);

  return { success: true };
}

/**
 * Generate and download a PDF listing every client and their custody balances
 * (USD / EUR / SRD). Clients whose balances are all zero are excluded.
 *
 * Uses the single all-clients-saldos endpoint (one query on the server) so the
 * totals match the "All clients balance" popup and no client is dropped.
 * @param {(key: string) => string} t Translation function
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function exportAllClientsSaldosPDF(t) {
  const res = await walletService.getAllClientsSaldos();
  if (!res.success || !res.data || !Array.isArray(res.data.clients)) {
    return { success: false, message: res.message || 'Failed to load clients' };
  }

  // Drop all-zero clients; keep the rest.
  const rows = res.data.clients
    .map((c) => ({ name: c.name || `#${c.id}`, USD: c.USD || 0, EUR: c.EUR || 0, SRD: c.SRD || 0 }))
    .filter((r) => r.USD !== 0 || r.EUR !== 0 || r.SRD !== 0);

  // Sort alphabetically for a stable, scannable report.
  rows.sort((a, b) => a.name.localeCompare(b.name));

  const logo = await loadLogoDataUrl();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  const y = drawBrandedHeader(doc, t('saldos_statement'), logo, marginX, pageWidth, t);

  const totals = rows.reduce(
    (acc, r) => ({ USD: acc.USD + r.USD, EUR: acc.EUR + r.EUR, SRD: acc.SRD + r.SRD }),
    { USD: 0, EUR: 0, SRD: 0 }
  );

  const head = [[t('client'), 'USD', 'EUR', 'SRD']];
  const body = rows.map((r) => [
    r.name,
    formatAmount(r.USD),
    formatAmount(r.EUR),
    formatAmount(r.SRD),
  ]);
  const foot = [[t('total'), formatAmount(totals.USD), formatAmount(totals.EUR), formatAmount(totals.SRD)]];

  autoTable(doc, {
    startY: y + 10,
    head,
    body,
    foot,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 10, cellPadding: 6, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: BRAND.rowAlt, textColor: BRAND.text, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BRAND.rowAlt },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 100, halign: 'right' },
      2: { cellWidth: 100, halign: 'right' },
      3: { cellWidth: 100, halign: 'right' },
    },
    didParseCell: (data) => {
      // Numeric columns (index 1-3): right-align in every section so the
      // footer totals line up under the body amounts, and color by sign.
      if (data.column.index >= 1 && (data.section === 'body' || data.section === 'foot')) {
        data.cell.styles.halign = 'right';
        const raw = data.cell.raw || '';
        if (typeof raw === 'string' && raw.trim().startsWith('-')) {
          data.cell.styles.textColor = BRAND.negative;
        } else if (data.section === 'body') {
          data.cell.styles.textColor = BRAND.positive;
        }
      }
    },
    didDrawPage: () => {
      drawBrandedFooter(doc, marginX, pageWidth, t);
    },
  });

  if (rows.length === 0) {
    const afterY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(t('no_clients_with_balance'), marginX, afterY + 24);
    doc.setTextColor(0);
  }

  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`saldos_${dateStamp}.pdf`);

  return { success: true };
}

/**
 * Generate and download a PDF of a customer's full loan transaction history.
 * Mirrors exportCustomerTransactionsPDF. Balances shown are outstanding loan amounts.
 * @param {string|number} customerId
 * @param {(key: string) => string} t Translation function
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function exportCustomerLoansPDF(customerId, t) {
  const result = await loanService.getTransactionsExport(customerId);

  if (!result.success || !result.data) {
    return { success: false, message: result.message || 'Failed to load export data' };
  }

  const { customer, balances = {}, transactions = [] } = result.data;

  const logo = await loadLogoDataUrl();

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Branded header (logo + store + title + accent rule)
  let y = drawBrandedHeader(doc, t('loan_statement'), logo, marginX, pageWidth, t);

  // Customer info block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.text);
  doc.text(customer.name || t('unknown_customer'), marginX, y);

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);

  if (customer.phone) {
    doc.text(`${t('phone')}: ${customer.phone}`, marginX, y);
    y += 14;
  }
  if (customer.info) {
    const infoLines = doc.splitTextToSize(`${t('additional_info')}: ${customer.info}`, pageWidth - marginX * 2);
    doc.text(infoLines, marginX, y);
    y += 14 * infoLines.length;
  }
  y += 18;

  // Current outstanding balances summary
  const balanceParts = Object.keys(balances).length
    ? Object.entries(balances).map(([cur, amt]) => `${cur}: ${formatAmount(amt)}`)
    : [t('no_loans')];
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.primary);
  doc.text(t('outstanding_balances'), marginX, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.text);
  doc.text(balanceParts.join('    '), marginX, y);
  doc.setTextColor(0);
  y += 10;

  // Transactions table — track running outstanding balance per currency
  const running = { USD: 0, EUR: 0, SRD: 0 };
  const head = [[t('date'), t('type'), t('description'), t('amount'), 'USD', 'EUR', 'SRD']];
  const body = transactions.map((tx) => {
    const amount = parseFloat(tx.amount);
    const sign = amount >= 0 ? '+' : '-';
    const cur = tx.currency;

    if (cur in running) {
      running[cur] = parseFloat(tx.balance_after);
    }

    return [
      formatDate(tx.created_at),
      txTypeLabel(tx.type, t),
      tx.description || '-',
      `${sign}${formatAmount(Math.abs(amount))} ${cur}`,
      formatAmount(running.USD),
      formatAmount(running.EUR),
      formatAmount(running.SRD),
    ];
  });

  autoTable(doc, {
    startY: y + 10,
    head,
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BRAND.rowAlt },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 90 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 105, halign: 'right' },
      4: { cellWidth: 75, halign: 'right' },
      5: { cellWidth: 75, halign: 'right' },
      6: { cellWidth: 75, halign: 'right' },
    },
    didParseCell: (data) => {
      // Disbursement (+) = debt up = red; repayment (-) = debt down = green
      if (data.section === 'body' && data.column.index === 3) {
        const raw = data.cell.raw || '';
        if (typeof raw === 'string' && raw.startsWith('-')) {
          data.cell.styles.textColor = BRAND.positive;
        } else {
          data.cell.styles.textColor = BRAND.negative;
        }
      }
    },
    didDrawPage: () => {
      drawBrandedFooter(doc, marginX, pageWidth, t);
    },
  });

  if (transactions.length === 0) {
    const afterY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(t('no_transactions_found_for_customer'), marginX, afterY + 24);
    doc.setTextColor(0);
  }

  const safeName = (customer.name || 'customer').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`loan_statement_${safeName}_${dateStamp}.pdf`);

  return { success: true };
}

// Grams: 3 decimals (matches GoldDashboard formatGrams).
function formatGrams(n) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function formatPurity(p) {
  return (parseFloat(p) || 0).toFixed(2);
}

/**
 * Generate and download a branded PDF of a customer's gold holdings + history.
 * Gold is tracked per purity lot (grams). Holdings summary lists current grams
 * per purity; the table shows each transaction's signed grams and the lot's
 * balance after.
 * @param {string|number} customerId
 * @param {(key: string) => string} t Translation function
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function exportCustomerGoldPDF(customerId, t) {
  const result = await goldService.getGoldExport(customerId);

  if (!result.success || !result.data) {
    return { success: false, message: result.message || 'Failed to load export data' };
  }

  const { customer, lots = [], transactions = [] } = result.data;

  const logo = await loadLogoDataUrl();

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Branded header
  let y = drawBrandedHeader(doc, t('gold_statement'), logo, marginX, pageWidth, t);

  // Customer info block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND.text);
  doc.text(customer.name || t('unknown_customer'), marginX, y);

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);

  if (customer.phone) {
    doc.text(`${t('phone')}: ${customer.phone}`, marginX, y);
    y += 14;
  }
  if (customer.info) {
    const infoLines = doc.splitTextToSize(`${t('additional_info')}: ${customer.info}`, pageWidth - marginX * 2);
    doc.text(infoLines, marginX, y);
    y += 14 * infoLines.length;
  }
  y += 18;

  // Current holdings summary (grams per purity + total)
  const totalGrams = lots.reduce((sum, l) => sum + parseFloat(l.amount_grams || 0), 0);
  const holdingParts = lots.length
    ? lots.map((l) => `${formatPurity(l.purity)}%: ${formatGrams(l.amount_grams)} g`)
    : [t('no_gold_lots')];

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.primary);
  doc.text(`${t('gold_holdings')} — ${t('total')}: ${formatGrams(totalGrams)} g`, marginX, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.text);
  const holdingLines = doc.splitTextToSize(holdingParts.join('     '), pageWidth - marginX * 2);
  doc.text(holdingLines, marginX, y);
  doc.setTextColor(0);
  y += 14 * holdingLines.length;

  // Transactions table
  const head = [[t('date'), t('type'), t('purity'), t('description'), t('amount'), t('balance')]];
  const body = transactions.map((tx) => {
    const grams = parseFloat(tx.amount_grams);
    const sign = grams >= 0 ? '+' : '-';
    return [
      formatDate(tx.created_at),
      txTypeLabel(tx.type, t),
      `${formatPurity(tx.purity)}%`,
      tx.description || '-',
      `${sign}${formatGrams(Math.abs(grams))} g`,
      `${formatGrams(tx.balance_after)} g`,
    ];
  });

  autoTable(doc, {
    startY: y + 10,
    head,
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BRAND.rowAlt },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 80 },
      2: { cellWidth: 70, halign: 'center' },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 110, halign: 'right' },
      5: { cellWidth: 110, halign: 'right' },
    },
    didParseCell: (data) => {
      // Deposit (+grams) green, withdrawal (-grams) red
      if (data.section === 'body' && data.column.index === 4) {
        const raw = data.cell.raw || '';
        if (typeof raw === 'string' && raw.startsWith('-')) {
          data.cell.styles.textColor = BRAND.negative;
        } else {
          data.cell.styles.textColor = BRAND.positive;
        }
      }
    },
    didDrawPage: () => {
      drawBrandedFooter(doc, marginX, pageWidth, t);
    },
  });

  if (transactions.length === 0) {
    const afterY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(t('no_transactions_found_for_customer'), marginX, afterY + 24);
    doc.setTextColor(0);
  }

  const safeName = (customer.name || 'customer').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`gold_statement_${safeName}_${dateStamp}.pdf`);

  return { success: true };
}
